import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import campService from '../services/campService';
import { setSelectedCamp as setReduxSelectedCamp } from '../../../redux/slices/campSlice';
import Login from '../../../components/Login';
import '../../SharedTheme.css';

const CampSelection = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const centerId = searchParams.get('centerId');
  const country = searchParams.get('country') || 'USA';
  const fetchingRef = useRef(false);

  const { isAuthenticated } = useSelector((state) => state.auth);
  const centerData = useSelector((state) => state.center);

  const [camps, setCamps] = useState([]);
  const [selectedCamp, setSelectedCamp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showLogin, setShowLogin] = useState(false);
  const [showFlyerModal, setShowFlyerModal] = useState(false);

  const SUMMER_CAMP_IMG = 'https://stssevastorage.blob.core.windows.net/ykportal/summer-camp.png';

  const getAccomFee = (camp) =>
    camp?.accommodation_Fees || camp?.Accommodation_Fees || camp?.accommodationFees ||
    camp?.accommodation_fees || camp?.Accommodation_Fee || camp?.accommodationFee || 0;

  const hasDiscount = (camp) =>
    camp?.camp_Has_Discount || camp?.Camp_Has_Discount || camp?.campHasDiscount || false;

  const formatTime = (t) => {
    if (!t) return null;
    const str = typeof t === 'string' ? t : String(t);
    const match = str.match(/^(\d{1,2}):(\d{2})/);
    if (!match) return t;
    let h = parseInt(match[1]), m = match[2];
    const ampm = h >= 12 ? 'PM' : 'AM';
    h = h % 12 || 12;
    return `${h}:${m} ${ampm}`;
  };

  const getCampTimeFrom = (camp) =>
    formatTime(camp?.camp_Time_From || camp?.Camp_Time_From || camp?.campTimeFrom) || '8:00 AM';

  const getCampTimeTo = (camp) =>
    formatTime(camp?.camp_Time_To || camp?.Camp_Time_To || camp?.campTimeTo) || '3:00 PM';

  useEffect(() => {
    if (!centerId) return;
    if (fetchingRef.current) return;
    fetchCamps();
  }, [centerId, country]);

  const fetchCamps = async () => {
    fetchingRef.current = true;
    try {
      setLoading(true);
      const data = await campService.getCampsByCenter(centerId, country);
      setCamps(data || []);
      setError(null);
    } catch (err) {
      setError('Failed to load camps. Please try again later.');
      console.error(err);
    } finally {
      setLoading(false);
      fetchingRef.current = false;
    }
  };

  const isPastCamp = (camp) => {
    if (!camp.camp_Date_To) return false;
    return new Date(camp.camp_Date_To) < new Date();
  };

  const handleSelectCamp = (camp) => {
    if (isPastCamp(camp)) return;
    setSelectedCamp(camp);
    dispatch(setReduxSelectedCamp(camp));
  };

  const handleProceedToRegistration = () => {
    if (!selectedCamp) return;
    if (isAuthenticated) {
      navigate(`/camp/registration?campId=${selectedCamp.camp_Id}`);
    } else {
      setShowLogin(true);
    }
  };

  const handleLoginSuccess = () => {
    setShowLogin(false);
    if (selectedCamp) {
      navigate(`/camp/registration?campId=${selectedCamp.camp_Id}`);
    }
  };

  const handleLoginCancel = () => setShowLogin(false);

  // Lock body scroll when drawer is open
  React.useEffect(() => {
    if (selectedCamp) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [selectedCamp]);

  return (
    <div className="camp-selection-page yk-page-bg min-h-screen py-8">
      <div className="container-custom">

        {/* Header — matches ClassSelection */}
        <div className="mb-12">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-2xl md:text-3xl font-semibold text-gray-700 mb-2">
                Choose Your Camp
              </h1>
              {centerData?.center_Name && (
                <p className="text-sm text-gray-500">
                  📍 {centerData.center_Name}
                </p>
              )}
            </div>
            <button
              onClick={() => navigate('/camp/center')}
              className="yk-btn font-semibold py-2 px-6 rounded-full flex items-center gap-2"
            >
              ← Back
            </button>
          </div>
        </div>

        {/* Loading */}
        {loading && (
          <div className="text-center py-12">
            <div className="inline-block">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
            <p className="mt-4 text-gray-600">Loading camps...</p>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-6 mb-6 text-center">
            <p className="text-red-800 font-semibold mb-1">{error}</p>
            <p className="text-red-600 text-sm mb-4">The server took too long to respond or is temporarily unavailable.</p>
            <button
              onClick={() => { setError(null); fetchingRef.current = false; fetchCamps(); }}
              className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white font-semibold px-5 py-2 rounded-full transition text-sm"
            >
              🔄 Try Again
            </button>
          </div>
        )}

        {/* Camp Tiles Grid */}
        {!loading && camps.length > 0 && (
          <div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8 mb-12">
              {camps.map((camp, index) => {
                const past = isPastCamp(camp);
                return (
                  <div
                    key={camp.camp_Id}
                    onClick={() => handleSelectCamp(camp)}
                    className={`item-card relative ${past ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'} ${selectedCamp?.camp_Id === camp.camp_Id ? 'selected' : ''}`}
                    style={{ animationDelay: `${index * 100}ms` }}
                  >
                    {/* Selected tick */}
                    {selectedCamp?.camp_Id === camp.camp_Id && (
                      <div className="absolute top-3 right-3 z-20 w-9 h-9 bg-gradient-to-br from-yellow-400 to-amber-500 rounded-full flex items-center justify-center shadow-lg animate-scale-in">
                        <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                    )}

                    {/* "Registration Closed" badge for past camps */}
                    {past && (
                      <div className="absolute top-3 left-3 z-20 bg-gray-700 text-white text-xs font-bold px-3 py-1 rounded-full shadow">
                        Registration Closed
                      </div>
                    )}

                    {/* Shine overlay */}
                    <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none">
                      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
                    </div>

                    {/* Image */}
                    <div className="relative w-full h-52 bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50 flex items-center justify-center rounded-t-[18px] overflow-hidden">
                      <img
                        src={camp.camp_Image_Url?.trim() || SUMMER_CAMP_IMG}
                        alt={camp.camp_Name}
                        loading="lazy"
                        decoding="async"
                        className="w-full h-full object-contain p-3 transition-transform duration-500 group-hover:scale-105"
                        onError={(e) => { e.target.onerror = null; e.target.src = SUMMER_CAMP_IMG; }}
                      />
                    </div>

                    {/* Content */}
                    <div className="p-6 bg-gradient-to-b from-white to-gray-50/50">
                      <h3 className="text-base font-semibold text-gray-800 mb-3 line-clamp-3 leading-snug">
                        {camp.camp_Name}
                      </h3>
                      {camp.camp_Date_From && (
                        <div className="mb-3 text-sm text-gray-600">
                          � {new Date(camp.camp_Date_From).toLocaleDateString()} – {new Date(camp.camp_Date_To).toLocaleDateString()}
                        </div>
                      )}
                      <button className="yk-btn w-full font-semibold py-3 px-5 rounded-full text-base">
                        View Details
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Side Drawer — same as ClassSelection */}
            {selectedCamp && (
              <>
                {/* Backdrop */}
                <div
                  className="fixed inset-0 bg-black/50 z-40 animate-fadeIn"
                  onClick={() => setSelectedCamp(null)}
                />

                {/* Right Drawer */}
                <div className="yk-drawer fixed top-0 right-0 h-full w-full md:w-[500px] shadow-2xl z-50 overflow-y-auto animate-slideInRight flex flex-col">

                  {/* Hero image — full width, fills top */}
                  <div className="relative w-full bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50 flex-shrink-0" style={{minHeight:'300px'}}>
                    <img
                      src={selectedCamp.camp_Image_Url?.trim() || SUMMER_CAMP_IMG}
                      alt={selectedCamp.camp_Name}
                      loading="lazy"
                      decoding="async"
                      className="w-full h-full object-contain p-4"
                      style={{minHeight:'300px', maxHeight:'360px'}}
                      onError={(e) => { e.target.onerror = null; e.target.src = SUMMER_CAMP_IMG; }}
                    />
                    {/* Gradient overlay at bottom */}
                    <div className="absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-black/50 to-transparent" />
                    {/* Close button */}
                    <button
                      onClick={() => setSelectedCamp(null)}
                      className="absolute top-3 right-3 w-9 h-9 bg-black/40 hover:bg-black/60 text-white rounded-full flex items-center justify-center text-lg transition"
                    >✕</button>
                    {/* Camp name overlay */}
                    <div className="absolute bottom-3 left-4 right-4">
                      <h2 className="text-white text-xl font-extrabold drop-shadow-lg leading-tight line-clamp-2">{selectedCamp.camp_Name}</h2>
                    </div>
                  </div>

                  {/* Body */}
                  <div className="flex-1 p-5 space-y-4">

                    {/* Action buttons */}
                    <div className="flex gap-3">
                      <button
                        onClick={handleProceedToRegistration}
                        className="yk-btn flex-1 font-bold py-3 rounded-2xl text-sm flex items-center justify-center gap-2"
                      >
                        ⚡ Register Now
                      </button>
                      <button
                        onClick={() => setShowFlyerModal(true)}
                        className="yk-btn flex-1 font-bold py-3 rounded-2xl text-sm flex items-center justify-center gap-2"
                      >
                        🎪 View Flyer
                      </button>
                    </div>

                    {/* Info cards — white 3D tiles */}
                    <div className="grid grid-cols-2 gap-3">

                      {selectedCamp.camp_Date_From && selectedCamp.camp_Date_To && (
                        <div className="col-span-2 bg-white rounded-2xl px-4 py-3 flex items-center gap-3" style={{boxShadow:'0 4px 0 rgba(0,0,0,0.10),0 8px 20px rgba(0,0,0,0.07),inset 0 1px 0 rgba(255,255,255,1)'}}>
                          <span className="text-2xl">🗓️</span>
                          <div>
                            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Camp Dates</p>
                            <p className="font-black text-gray-900 text-base leading-snug">
                              {new Date(selectedCamp.camp_Date_From).toLocaleDateString('en-US',{month:'short',day:'numeric',year:'numeric'})} – {new Date(selectedCamp.camp_Date_To).toLocaleDateString('en-US',{month:'short',day:'numeric',year:'numeric'})}
                            </p>
                          </div>
                        </div>
                      )}

                      {selectedCamp.camp_Fee >= 0 && (
                        <div className="bg-white rounded-2xl px-4 py-3" style={{boxShadow:'0 4px 0 rgba(0,0,0,0.10),0 8px 20px rgba(0,0,0,0.07),inset 0 1px 0 rgba(255,255,255,1)'}}>
                          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">💰 Weekly Fee</p>
                          <p className="font-black text-gray-900 text-3xl leading-none">${selectedCamp.camp_Fee || 0}</p>
                          <p className="text-xs text-gray-500 mt-1">per week</p>
                        </div>
                      )}

                      <div className="bg-white rounded-2xl px-4 py-3" style={{boxShadow:'0 4px 0 rgba(0,0,0,0.10),0 8px 20px rgba(0,0,0,0.07),inset 0 1px 0 rgba(255,255,255,1)'}}>
                        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">⏰ Regular Hours</p>
                        <p className="font-black text-gray-900 text-base leading-snug">{getCampTimeFrom(selectedCamp)} – {getCampTimeTo(selectedCamp)}</p>
                        <p className="text-xs text-gray-500 mt-1">Mon – Fri daily</p>
                      </div>

                      {getAccomFee(selectedCamp) > 0 && (
                        <div className="bg-white rounded-2xl px-4 py-3" style={{boxShadow:'0 4px 0 rgba(0,0,0,0.10),0 8px 20px rgba(0,0,0,0.07),inset 0 1px 0 rgba(255,255,255,1)'}}>
                          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">🏕️ Extended Stay</p>
                          <p className="font-black text-gray-900 text-3xl leading-none">+${getAccomFee(selectedCamp)}</p>
                          <p className="text-xs text-gray-500 mt-1">Extended Hours: 3 PM – 6 PM</p>
                        </div>
                      )}

                      {hasDiscount(selectedCamp) && (
                        <div className="bg-white rounded-2xl px-4 py-3" style={{boxShadow:'0 4px 0 rgba(0,0,0,0.10),0 8px 20px rgba(0,0,0,0.07),inset 0 1px 0 rgba(255,255,255,1)'}}>
                          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">�️ Multi-week</p>
                          <p className="font-black text-gray-900 text-3xl leading-none">10% OFF</p>
                          <p className="text-xs text-gray-500 mt-1">Book 2+ weeks & save</p>
                        </div>
                      )}

                    </div>

                    {/* Contact info */}
                    <div className="bg-gray-50 border border-gray-100 rounded-2xl p-4 space-y-3">
                      <p className="text-xs font-bold text-gray-400 uppercase tracking-widest">Contact & Location</p>
                      {selectedCamp.camp_PhoneNo && (
                        <div className="flex items-center gap-3">
                          <span className="w-8 h-8 bg-blue-100 rounded-xl flex items-center justify-center text-sm flex-shrink-0">📞</span>
                          <div>
                            <p className="text-xs text-gray-500">Phone</p>
                            <p className="font-semibold text-gray-800 text-sm">{selectedCamp.camp_PhoneNo}</p>
                          </div>
                        </div>
                      )}
                      {selectedCamp.camp_Email && (
                        <div className="flex items-center gap-3">
                          <span className="w-8 h-8 bg-green-100 rounded-xl flex items-center justify-center text-sm flex-shrink-0">✉️</span>
                          <div>
                            <p className="text-xs text-gray-500">Email</p>
                            <p className="font-semibold text-gray-800 text-sm break-all">{selectedCamp.camp_Email}</p>
                          </div>
                        </div>
                      )}
                      {selectedCamp.camp_Address && (
                        <div className="flex items-start gap-3">
                          <span className="w-8 h-8 bg-red-100 rounded-xl flex items-center justify-center text-sm flex-shrink-0 mt-0.5">📍</span>
                          <div>
                            <p className="text-xs text-gray-500">Address</p>
                            <p className="font-semibold text-gray-800 text-sm">
                              {['yugalkunj','yugal kunj','yugalkunj duluth'].includes((selectedCamp.camp_Address||'').toLowerCase().trim())
                                ? 'Yugal Kunj, 2769 Duluth Hwy, Duluth, GA 30096'
                                : selectedCamp.camp_Address}
                            </p>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Description */}
                    {selectedCamp.camp_Description && !['<P><Br></P>','<p><br></p>'].includes(selectedCamp.camp_Description?.trim()) && (
                      <div className="bg-yellow-50 border border-yellow-100 rounded-2xl p-4">
                        <p className="text-xs font-bold text-yellow-600 uppercase tracking-widest mb-2">About this Camp</p>
                        <div className="text-sm text-gray-700 leading-relaxed" dangerouslySetInnerHTML={{__html: selectedCamp.camp_Description}} />
                      </div>
                    )}

                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {/* Empty State */}
        {!loading && camps.length === 0 && !error && (
          <div className="text-center py-12">
            <p className="text-gray-600 text-lg">No camps available for this center.</p>
          </div>
        )}

        {/* ── Flyer Modal ── */}
        {showFlyerModal && selectedCamp && (
          <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[60] flex items-center justify-center p-4">
            <div className="bg-white rounded-3xl shadow-2xl w-full max-w-lg overflow-hidden flex flex-col" style={{maxHeight:'95vh'}}>

              {/* Full flyer image — takes most of the modal */}
              <div className="relative flex-1 bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50 flex items-center justify-center overflow-hidden" style={{minHeight:'420px'}}>
                <img
                  src={selectedCamp.camp_Image_Url?.trim() || SUMMER_CAMP_IMG}
                  alt={selectedCamp.camp_Name}
                  loading="eager"
                  className="w-full h-full object-contain"
                  style={{maxHeight:'520px'}}
                  onError={(e) => { e.target.onerror = null; e.target.src = SUMMER_CAMP_IMG; }}
                />
                {/* Close button */}
                <button
                  onClick={() => setShowFlyerModal(false)}
                  className="absolute top-3 right-3 w-9 h-9 bg-black/50 hover:bg-black/70 text-white rounded-full flex items-center justify-center text-lg transition z-10"
                >✕</button>
              </div>

              {/* Compact info strip */}
              <div className="bg-white px-5 py-4 border-t border-gray-100">
                <p className="font-bold text-gray-800 text-base mb-3 leading-tight">{selectedCamp.camp_Name}</p>
                {/* Inline info row */}
                <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm mb-4">
                  {selectedCamp.camp_Date_From && (
                    <span className="flex items-center gap-1 text-purple-700 font-semibold">
                      📅 {new Date(selectedCamp.camp_Date_From).toLocaleDateString('en-US',{month:'short',day:'numeric'})} – {new Date(selectedCamp.camp_Date_To).toLocaleDateString('en-US',{month:'short',day:'numeric',year:'numeric'})}
                    </span>
                  )}
                  {selectedCamp.camp_Fee > 0 && (
                    <span className="flex items-center gap-1 text-orange-600 font-bold">
                      💰 ${selectedCamp.camp_Fee}/week
                    </span>
                  )}
                  {selectedCamp.camp_PhoneNo && (
                    <span className="flex items-center gap-1 text-blue-700 font-semibold">
                      📞 {selectedCamp.camp_PhoneNo}
                    </span>
                  )}
                  {selectedCamp.camp_Email && (
                    <span className="flex items-center gap-1 text-green-700 font-semibold">
                      ✉️ {selectedCamp.camp_Email}
                    </span>
                  )}
                </div>
                {/* Register CTA */}
                <button
                  onClick={() => { setShowFlyerModal(false); handleProceedToRegistration(); }}
                  className="yk-btn w-full font-bold py-3 rounded-2xl text-sm"
                >
                  ⚡ Register for this Camp
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Login Modal */}
        {showLogin && (
          <Login
            onLoginSuccess={handleLoginSuccess}
            onCancel={handleLoginCancel}
            entity="RMS"
          />
        )}
      </div>
    </div>
  );
};

export default CampSelection;
